import { usePrefersReducedMotion, useTheme } from "@chakra-ui/react";
//@ts-ignore
import Mazemap from "@itpi/mazemap";
import { Geometry } from "geojson";
import { FitBoundsOptions, FlyToOptions } from "mapbox-gl";
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { defaultLocation, MapPin } from "../components/Map";
import { Gig, Venue } from "../types/MusicFestivalTypes";

export type MapContextState = {
  reset: () => void;
  setPins: (pins: MapPin[]) => void;
  setMap: (map: any) => void;
  flyTo: (opts: FlyToOptions) => void;
  setOffset: (offset: [number, number]) => void;
  fitBounds: (bounds: Geometry, opts: FitBoundsOptions) => void;
  watchLocation: () => void;
  setUserLocation: React.Dispatch<React.SetStateAction<{
    lng: number;
    lat: number;
    accuracy: number;
} | undefined>>
  venues: Venue[],
  setVenues: React.Dispatch<React.SetStateAction<Venue[]>>,
  gigs: Gig[],
  setFloorLevel: React.Dispatch<React.SetStateAction<number>>
  setGigs: React.Dispatch<React.SetStateAction<Gig[]>>,
  pins: MapPin[];
  locationError: boolean;
  personalLocation?: GeolocationPosition;
  map?: any;
  location?: { lng: number; lat: number };
  routeController?: any;
  highlighter?: any;
};

export const MapContext = createContext<MapContextState>({
  reset: () => null,
  setMap: () => null,
  flyTo: () => null,
  fitBounds: () => null,
  setPins: () => null,
  setOffset: () => null,
  watchLocation: () => null,
  setVenues: () => null,
  setUserLocation: () => null,
  setFloorLevel: () => null,
  setGigs: () => null,
  gigs: [],
  venues: [],
  locationError: false,
  pins: [],
});

type Props = {
  children: React.ReactNode;
};

function usePrevious(value: any) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export const MapProvider = ({ children }: Props) => {
  const [map, setMap] = useState<any>();
  const [location, setLocation] = useState<{ lng: number; lat: number }>();
  const [highlighter, setHighlighter] = useState<any>();
  const [pins, setPins] = useState<MapPin[]>([]);
  const [routeController, setRouteController] = useState<any>();
  const [userLocation, setUserLocation] = useState<{ lng: number; lat: number, accuracy: number}>();
  const [offset, setOffset] = useState<[number, number]>([0, 0]);
  const [boundsOffset, setBoundsOffset] = useState<[number, number]>([0, 0]);
  const [flyToTarget, setFlyToTarget] = useState<FlyToOptions | undefined>(
    defaultLocation
  );
  const [floorLevel, setFloorLevel]= useState<number>(1);
  const [venues, setVenues] = useState<Venue[]>([])
  const [gigs, setGigs] = useState<Gig[]>([])
  const [personalLocation, setPersonalLocation] =
    useState<GeolocationPosition>();
  const [fitBoundsTarget, setFitBoundsTarget] = useState<
    { geom: Geometry; options: FitBoundsOptions } | undefined
  >();
  const [watchLocationId, setWatchLocationId] = useState<number>();
  const theme = useTheme();
  const [mapReady, setMapReady] = useState(false);
  const [locationDot, setLocationDot] = useState<Mazemap.BlueDot>();
  const [locationError, setLocationError] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  const speed = useMemo(() => {
    return prefersReducedMotion ? 9999999999 : 1;
  }, [prefersReducedMotion]);

  useEffect(() => {
    setBoundsOffset([offset[0] * 0.5, offset[1] * 0.5]);
  }, [offset]);

  const flyTo = useCallback((opts: FlyToOptions) => {
    setFlyToTarget(opts);
    setFitBoundsTarget(undefined);
  }, []);

  useEffect(() => {
    if (map) {
      map.on("load", () => {
        const rc = new Mazemap.RouteController(map, {
          routeLineColorPrimary: theme.colors?.["lancsBlue"]?.["500"],
          routeLineColorSecondary: theme.colors?.["lancsBlue"]?.["900"],
        });
        const hl = new Mazemap.Highlighter(map, {
          showOutline: true,
          showFill: true,
          outlineColor: Mazemap.Util.Colors.MazeColors.MazeBlue,
          fillColor: Mazemap.Util.Colors.MazeColors.MazeBlue,
        });
        setRouteController(rc);
        setHighlighter(hl);
        setMapReady(true);
      });

      map.on("moveend", (e: any) => {
        setLocation(map.getCenter());
      });

      map.on("dragend", (e: any) => {
        setFlyToTarget({
          center: map.getCenter(),
          zoom: map.getZoom(),
          pitch: map.getPitch(),
          bearing: map.getBearing(),
          offset: [0, 0],
          speed,
        });
      });

      map.on("touchend", (e: any) => {
        setFlyToTarget({
          center: map.getCenter(),
          zoom: map.getZoom(),
          pitch: map.getPitch(),
          bearing: map.getBearing(),
          offset: [0, 0],
          speed,
        });
      });
    }
  }, [map, theme.colors, flyTo]);

  useEffect(() => {
    if (personalLocation && mapReady && map) {
      if (locationDot) {
        locationDot.setLngLatAnimated({
          lng: personalLocation.coords.longitude,
          lat: personalLocation.coords.latitude,
        });
        locationDot.setAccuracy(personalLocation.coords.accuracy);
        locationDot.show();
      } else {
        setLocationDot(new Mazemap.BlueDot({ map }));
      }
    }
  }, [personalLocation, mapReady, locationDot, map]);

  // Plots the users location if provided - along with an accuracy 
  useEffect(() => {
    if (userLocation && mapReady && map) {
      if (locationDot) {
        locationDot.setLngLatAnimated({
          lng: userLocation.lng,
          lat: userLocation.lat,
        });
        locationDot.setAccuracy(userLocation.accuracy);
        locationDot.setZLevel(floorLevel)
        locationDot.show();
      } else {
        setLocationDot(new Mazemap.BlueDot({ map }));

      }
    }
  }, [userLocation, mapReady, locationDot, map, floorLevel])




  useEffect(() => {
    if (map && fitBoundsTarget) {
      map.fitBounds(fitBoundsTarget.geom, {
        ...fitBoundsTarget.options,
        speed,
        offset: boundsOffset,
      });
    }
  }, [map, fitBoundsTarget, boundsOffset]);

  useEffect(() => {
    if (map && flyToTarget) {
      map.flyTo({
        offset,
        speed,
        ...flyToTarget,
      });
    }
  }, [offset, map, flyToTarget]);

  const fitBounds = useCallback((geom: Geometry, options: FitBoundsOptions) => {
    setFitBoundsTarget({ geom, options });
    setFlyToTarget(undefined);
  }, []);

  const reset = useCallback(() => {
    flyTo(defaultLocation);
    map?.setZLevel(1);
    highlighter?.clear();
    setPins([]);
  }, [highlighter, flyTo, map]);

  const watchLocation = useCallback(() => {
    if (!watchLocationId) {
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          setPersonalLocation(position);
          setLocationError(false);
        },
        () => setLocationError(true)
      );
      setWatchLocationId(watchId);
    }
  }, [watchLocationId]);

  return (
    <MapContext.Provider
      value={{
        map,
        highlighter,
        pins,
        location,
        routeController,
        personalLocation,
        locationError,
        venues,
        gigs,
        setGigs,
        setVenues,
        watchLocation,
        setFloorLevel,
        setMap,
        setPins,
        setUserLocation,
        reset,
        fitBounds,
        setOffset,
        flyTo,
      }}
    >
      {children}
    </MapContext.Provider>
  );
};
